/**
 * AI Sidebar Component
 * Slide-in panel with chat interface for AI assistant
 */

interface ChatMessage {
    role: 'user' | 'ai';
    content: string;
    timestamp: Date;
}

export class AISidebar {
    private container: HTMLElement;
    private messagesDiv!: HTMLElement;
    private input!: HTMLInputElement;
    private isOpen: boolean = false;
    private serverUrl: string;
    private messages: ChatMessage[] = [];
    private isLoading: boolean = false;

    constructor(parentElement: HTMLElement, serverUrl: string) {
        this.serverUrl = serverUrl;
        this.container = document.createElement('div');
        this.container.id = 'ai-sidebar';
        this.container.innerHTML = this.createHTML();
        parentElement.appendChild(this.container);

        this.injectStyles();
        this.bindElements();
        this.attachEventListeners();
    }

    /**
     * Toggle sidebar open/closed
     */
    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Open the sidebar
     */
    open(): void {
        this.isOpen = true;
        this.container.classList.add('ai-sidebar-open');
        this.input.focus();
    }

    /**
     * Close the sidebar
     */
    close(): void {
        this.isOpen = false;
        this.container.classList.remove('ai-sidebar-open');
    }

    /**
     * Add a message to the chat from external source (e.g., screen analysis result)
     */
    addExternalMessage(role: 'user' | 'ai', content: string): void {
        this.addMessage(role, content);
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private async sendMessage(text: string): Promise<void> {
        if (!text.trim() || this.isLoading) return;

        this.addMessage('user', text);
        this.setLoading(true);

        try {
            // Try streaming endpoint first
            const streamResponse = await fetch(`${this.serverUrl}/api/ai/chat/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: text,
                    systemPrompt: 'You are an AI assistant helping with a remote desktop session. Be concise and helpful.'
                })
            });

            if (streamResponse.ok && streamResponse.body) {
                await this.handleStreamResponse(streamResponse);
                return;
            }
        } catch {
            // Stream not available, fall back to regular endpoint
        }

        try {
            const response = await fetch(`${this.serverUrl}/api/ai/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: text,
                    systemPrompt: 'You are an AI assistant helping with a remote desktop session. Be concise and helpful.'
                })
            });

            if (response.ok) {
                const data = await response.json();
                this.addMessage('ai', data.content || data.response || 'No response received.');
            } else {
                this.addMessage('ai', `Error: Server returned ${response.status}. The AI chat endpoint may not be configured yet.`);
            }
        } catch (error: any) {
            this.addMessage('ai', `Connection error: ${error.message || 'Could not reach the server.'}`);
        } finally {
            this.setLoading(false);
        }
    }

    private async handleStreamResponse(response: Response): Promise<void> {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let aiContent = '';

        // Create the AI message bubble for streaming
        const bubbleEl = this.createMessageElement('ai', '');
        this.messagesDiv.appendChild(bubbleEl);
        this.scrollToBottom();

        const contentEl = bubbleEl.querySelector('.ai-msg-content') as HTMLElement;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                // Parse SSE data lines
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') break;
                        try {
                            const parsed = JSON.parse(data);
                            if (parsed.content) {
                                aiContent += parsed.content;
                                contentEl.textContent = aiContent;
                                this.scrollToBottom();
                            }
                        } catch {
                            // Not JSON, treat as plain text
                            aiContent += data;
                            contentEl.textContent = aiContent;
                            this.scrollToBottom();
                        }
                    }
                }
            }
        } catch {
            // Stream ended
        }

        if (aiContent) {
            this.messages.push({ role: 'ai', content: aiContent, timestamp: new Date() });
        }

        this.setLoading(false);
    }

    private addMessage(role: 'user' | 'ai', content: string): void {
        this.messages.push({ role, content, timestamp: new Date() });
        const el = this.createMessageElement(role, content);
        this.messagesDiv.appendChild(el);
        this.scrollToBottom();
    }

    private createMessageElement(role: 'user' | 'ai', content: string): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = `ai-msg ai-msg-${role}`;

        const bubble = document.createElement('div');
        bubble.className = `ai-msg-bubble ai-msg-bubble-${role}`;

        const contentEl = document.createElement('div');
        contentEl.className = 'ai-msg-content';
        contentEl.innerHTML = this.renderMarkdown(content);

        bubble.appendChild(contentEl);
        wrapper.appendChild(bubble);

        return wrapper;
    }

    private renderMarkdown(text: string): string {
        if (!text) return '';
        // Basic markdown rendering
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/\n/g, '<br>');
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
        const loadingEl = this.container.querySelector('.ai-loading') as HTMLElement;
        const sendBtn = this.container.querySelector('.ai-send-btn') as HTMLButtonElement;

        if (loadingEl) {
            loadingEl.style.display = loading ? 'flex' : 'none';
        }
        if (sendBtn) {
            sendBtn.disabled = loading;
        }
        this.input.disabled = loading;

        const statusEl = this.container.querySelector('.ai-status-text') as HTMLElement;
        if (statusEl) {
            statusEl.textContent = loading ? 'Thinking...' : 'Ready';
        }

        if (!loading) {
            this.input.focus();
        }
    }

    private scrollToBottom(): void {
        requestAnimationFrame(() => {
            this.messagesDiv.scrollTop = this.messagesDiv.scrollHeight;
        });
    }

    private bindElements(): void {
        this.messagesDiv = this.container.querySelector('.ai-messages') as HTMLElement;
        this.input = this.container.querySelector('.ai-input') as HTMLInputElement;
    }

    private attachEventListeners(): void {
        // Send button
        const sendBtn = this.container.querySelector('.ai-send-btn') as HTMLButtonElement;
        sendBtn.addEventListener('click', () => {
            const text = this.input.value.trim();
            if (text) {
                this.input.value = '';
                this.sendMessage(text);
            }
        });

        // Enter key
        this.input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const text = this.input.value.trim();
                if (text) {
                    this.input.value = '';
                    this.sendMessage(text);
                }
            }
        });

        // Close button
        const closeBtn = this.container.querySelector('.ai-close-btn') as HTMLButtonElement;
        closeBtn.addEventListener('click', () => this.close());

        // Escape key to close
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }

    private createHTML(): string {
        return `
            <div class="ai-sidebar-panel">
                <!-- Header -->
                <div class="ai-header">
                    <div class="ai-header-left">
                        <div class="ai-logo">AI</div>
                        <div class="ai-header-info">
                            <div class="ai-header-title">AI Assistant</div>
                            <div class="ai-status-text">Ready</div>
                        </div>
                    </div>
                    <button class="ai-close-btn" title="Close (Esc)">&times;</button>
                </div>

                <!-- Messages -->
                <div class="ai-messages">
                    <div class="ai-msg ai-msg-ai">
                        <div class="ai-msg-bubble ai-msg-bubble-ai">
                            <div class="ai-msg-content">
                                Hello! I'm your AI assistant for this remote desktop session. I can help with:
                                <br><br>
                                &#x2022; Troubleshooting connection issues<br>
                                &#x2022; Analyzing screen content<br>
                                &#x2022; System administration tips
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loading -->
                <div class="ai-loading" style="display: none;">
                    <div class="ai-msg-bubble ai-msg-bubble-ai">
                        <span class="ai-dot"></span>
                        <span class="ai-dot"></span>
                        <span class="ai-dot"></span>
                    </div>
                </div>

                <!-- Input -->
                <div class="ai-input-area">
                    <input class="ai-input" type="text" placeholder="Ask something..." autocomplete="off" />
                    <button class="ai-send-btn" title="Send">&#x27A4;</button>
                </div>
            </div>
        `;
    }

    private createCSS(): string {
        return `
            #ai-sidebar {
                position: fixed;
                top: 0;
                right: -360px;
                width: 350px;
                height: 100vh;
                z-index: 9999;
                transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                pointer-events: none;
            }

            #ai-sidebar.ai-sidebar-open {
                right: 0;
                pointer-events: auto;
            }

            .ai-sidebar-panel {
                width: 100%;
                height: 100%;
                background: #0F1923;
                border-left: 1px solid #1C2938;
                display: flex;
                flex-direction: column;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }

            /* Header */
            .ai-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 14px;
                background: #141E2A;
                border-bottom: 1px solid #1C2938;
                flex-shrink: 0;
            }

            .ai-header-left {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .ai-logo {
                width: 28px;
                height: 28px;
                border-radius: 8px;
                background: #1A3A2A;
                color: #00BF63;
                font-size: 10px;
                font-weight: bold;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .ai-header-title {
                color: white;
                font-size: 13px;
                font-weight: 600;
            }

            .ai-status-text {
                color: #5A7A6A;
                font-size: 10px;
            }

            .ai-close-btn {
                width: 28px;
                height: 28px;
                border: none;
                background: transparent;
                color: #8899AA;
                font-size: 18px;
                cursor: pointer;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .ai-close-btn:hover {
                background: #1C2938;
                color: white;
            }

            /* Messages */
            .ai-messages {
                flex: 1;
                overflow-y: auto;
                padding: 12px 8px;
            }

            .ai-messages::-webkit-scrollbar {
                width: 4px;
            }

            .ai-messages::-webkit-scrollbar-track {
                background: transparent;
            }

            .ai-messages::-webkit-scrollbar-thumb {
                background: #2A3A4A;
                border-radius: 2px;
            }

            .ai-msg {
                margin-bottom: 8px;
                display: flex;
                animation: ai-msg-in 0.2s ease-out;
            }

            .ai-msg-user {
                justify-content: flex-end;
            }

            .ai-msg-ai {
                justify-content: flex-start;
            }

            .ai-msg-bubble {
                max-width: 85%;
                padding: 10px 14px;
                font-size: 13px;
                line-height: 1.5;
                word-wrap: break-word;
            }

            .ai-msg-bubble-user {
                background: #1A6FC4;
                color: white;
                border-radius: 14px 14px 4px 14px;
            }

            .ai-msg-bubble-ai {
                background: #1C2938;
                color: #C0D0E0;
                border-radius: 14px 14px 14px 4px;
            }

            .ai-msg-content code {
                background: #0F1923;
                padding: 1px 4px;
                border-radius: 3px;
                font-family: 'Consolas', monospace;
                font-size: 12px;
            }

            /* Loading dots */
            .ai-loading {
                padding: 4px 8px;
                display: flex;
            }

            .ai-loading .ai-msg-bubble-ai {
                display: flex;
                gap: 4px;
                align-items: center;
                padding: 10px 16px;
            }

            .ai-dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: #00BF63;
                opacity: 0.3;
                animation: ai-dot-bounce 1.2s infinite;
            }

            .ai-dot:nth-child(2) { animation-delay: 0.2s; }
            .ai-dot:nth-child(3) { animation-delay: 0.4s; }

            @keyframes ai-dot-bounce {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-4px); }
            }

            @keyframes ai-msg-in {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }

            /* Input */
            .ai-input-area {
                display: flex;
                gap: 8px;
                padding: 10px;
                background: #141E2A;
                border-top: 1px solid #1C2938;
                flex-shrink: 0;
            }

            .ai-input {
                flex: 1;
                background: #0F1923;
                border: 1px solid #2A3A4A;
                border-radius: 8px;
                color: white;
                padding: 8px 12px;
                font-size: 13px;
                outline: none;
                font-family: inherit;
            }

            .ai-input:focus {
                border-color: #00BF63;
            }

            .ai-input::placeholder {
                color: #5A6A7A;
            }

            .ai-input:disabled {
                opacity: 0.5;
            }

            .ai-send-btn {
                width: 36px;
                height: 36px;
                border: none;
                background: #00BF63;
                color: white;
                border-radius: 8px;
                font-size: 14px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .ai-send-btn:hover {
                background: #00D96F;
            }

            .ai-send-btn:disabled {
                background: #2A3A4A;
                color: #5A6A7A;
                cursor: not-allowed;
            }

            /* Responsive */
            @media (max-width: 600px) {
                #ai-sidebar {
                    width: 100vw;
                    right: -100vw;
                }

                #ai-sidebar.ai-sidebar-open {
                    right: 0;
                }
            }
        `;
    }

    private injectStyles(): void {
        if (document.getElementById('ai-sidebar-styles')) return;
        const style = document.createElement('style');
        style.id = 'ai-sidebar-styles';
        style.textContent = this.createCSS();
        document.head.appendChild(style);
    }
}
